import { AmplitudeCatalogStatus, AmplitudeLotStatus } from '@/components/Analytics/Plugins/plugins.type';
import { BuyNowStatus, ItemSummary } from '@/types/item/ItemSummary';
import { AuctionStatus } from '@/types/LiveAuctionState';

/**
 * Return an analytics catalog status from the catalog state
 * @param catalogStatus {AuctionStatus} - The database value for the catalog status
 * @see {getCatalogStatus} - The selector to use to populate catalogStatus
 * @see {getCatalogIsPreview} - The selector to use to populate isCatalogPreview
 * @returns The "Title Cased" catalog status for use with amplitude
 */
export const getAmplitudeCatalogStatus = (catalogStatus: AuctionStatus): AmplitudeCatalogStatus => {
    if (catalogStatus === 'upcoming' || catalogStatus === 'online') {
        return AmplitudeCatalogStatus.Online;
    }
    if (catalogStatus === 'live' || catalogStatus === 'paused') {
        return AmplitudeCatalogStatus.Live;
    }
    if (catalogStatus === 'done' || catalogStatus === 'ended') {
        return AmplitudeCatalogStatus.Ended;
    }
    return AmplitudeCatalogStatus.None;
};

/**
 * Returns an analytics item status for the item state
 * @param buyNowStatus - The item summary's buy now status information
 * @param isPassed - Has the item been passed by the auction house
 * @param isSold - Has the item been sold to a bidder
 * @param isTimedPlusAuction - Is the item in a Timed+ auction
 * @param isPaused - Has the item been paused by the auction house
 * @param catalogStatus - The database value for the catalog status
 * @param isTimedPlusBiddingOption - Is the catalog a Timed+ auction?
 * @returns The "Title Cased" item status for use with amplitude
 */
export const getAmplitudeItemStatus = (
    buyNowStatus: ItemSummary['buyNowStatus'],
    isPassed: ItemSummary['isPassed'],
    isSold: ItemSummary['isSold'],
    isTimedPlusAuction: ItemSummary['isTimedPlusAuction'],
    isPaused: ItemSummary['isPaused'],
    catalogStatus: AuctionStatus,
    isTimedPlusBiddingOption: boolean
): AmplitudeLotStatus => {
    if (buyNowStatus === BuyNowStatus.Available) {
        return AmplitudeLotStatus.BuyNow;
    }
    if (isPassed) {
        return AmplitudeLotStatus.Passed;
    }
    if (isSold) {
        return AmplitudeLotStatus.Sold;
    }

    // region Timed+ Auctions
    if (isTimedPlusAuction) {
        if (isPaused) {
            return AmplitudeLotStatus.Passed;
        }
        return isTimedPlusBiddingOption ? AmplitudeLotStatus.Live : AmplitudeLotStatus.Online;
    }
    // endregion

    // region Live Auctions
    if (catalogStatus === 'online') {
        return AmplitudeLotStatus.Online;
    }
    if (catalogStatus === 'live') {
        return AmplitudeLotStatus.Live;
    }
    // endregion

    return AmplitudeLotStatus.None;
};
